'use client';

import type { GlobalFilterItem, UniversalSearchTabItemInfo } from '@weberus/shared-types';
import dynamic from 'next/dynamic';
import { useEffect, useMemo } from 'react';
import { clientLogProtobuf } from '@weberus/client-tracker/utils';
import { useAtomValue } from 'jotai';
import { useUniversalSearchCountQuery } from '@/app/(pages)/results/api/queries';
import { UniversalSearchCategory } from '@/app/(pages)/results/constants';
import { ProductListPageLog } from '@/app/_shared/protobuf-log/clientlogs/product-list-web-txtpb';
import {
  createGlobalFilterGlobalPriceRangeText,
  createGlobalFilterPriceRangeText,
} from '@/app/_shared/utils/booking-text-utils';
import { localCheckIOCapacityAtom } from '@/app/_shared/store/local-accommodation';
import { globalCheckIOCapacityAtom } from '@/app/_shared/store/global-accommodation';

const GlobalFilterModal = dynamic(
  () => import('@/app/_components/feature/global-filter-modal').then((mod) => mod.GlobalFilterModal),
  {
    ssr: false,
  },
);

const DEFAULT_CONTAINER_DETAIL = 'filterList';

interface GlobalFilterProps {
  open?: boolean;
  tabInfo?: UniversalSearchTabItemInfo;
  serverLogMeta?: unknown;
  onCloseModal?: () => void;
}

export const GlobalFilter = ({ open, tabInfo, serverLogMeta, onCloseModal }: GlobalFilterProps) => {
  const localCheckIOCapacity = useAtomValue(localCheckIOCapacityAtom);
  const globalCheckIOCapacity = useAtomValue(globalCheckIOCapacityAtom);

  const { data: searchCountData, isSuccess, isFetching } = useUniversalSearchCountQuery();

  useEffect(() => {
    // 국내숙소, 레저, 공연/전시는 상품 목록 조회전에 CTA 조회 결과로 상품이 없는 경우 No Result 로그 전송
    if (!open && isSuccess && !isFetching) {
      const selectedTabKey = tabInfo?.key;

      if (
        searchCountData?.count === 0 &&
        (selectedTabKey === UniversalSearchCategory.LOCAL_ACCOMMODATION ||
          selectedTabKey === UniversalSearchCategory.LOCAL_LEISURE ||
          selectedTabKey === UniversalSearchCategory.ENTERTAINMENT)
      ) {
        clientLogProtobuf(ProductListPageLog.SearchLabelNoAvailableProductMsg.impression, {
          position: 0,
          logMeta: searchCountData?.serverLogMeta,
        })();
      }
    }
  }, [searchCountData]);

  const globalFilterPriceRangeText = useMemo(() => {
    if (tabInfo?.key === 'LOCAL_ACCOMMODATION') {
      return createGlobalFilterPriceRangeText(localCheckIOCapacity);
    }

    if (tabInfo?.key === 'GLOBAL_ACCOMMODATION') {
      return createGlobalFilterGlobalPriceRangeText(globalCheckIOCapacity);
    }

    return '';
  }, [localCheckIOCapacity, globalCheckIOCapacity, tabInfo?.key]);

  const handleImprFilter = (item: GlobalFilterItem) => {
    clientLogProtobuf(ProductListPageLog.SearchLabelSectionName.impression, {
      containerDetail: DEFAULT_CONTAINER_DETAIL,
      logMeta: item.serverLogMeta,
    })();
  };

  const handleReset = () => {
    clientLogProtobuf(ProductListPageLog.SearchButtonReset.click, {
      containerDetail: DEFAULT_CONTAINER_DETAIL,
      logMeta: serverLogMeta,
    })();
  };

  const handleMoreClick = (serverLogMeta: GlobalFilterItem['serverLogMeta']) => {
    clientLogProtobuf(ProductListPageLog.SearchButtonMore.click, {
      containerDetail: DEFAULT_CONTAINER_DETAIL,
      logMeta: serverLogMeta,
    })();
  };

  const handleClickHistogram = (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => {
    clientLogProtobuf(ProductListPageLog.SearchSliderPriceRange.click, {
      containerDetail: DEFAULT_CONTAINER_DETAIL,
      logMeta: serverLogMeta,
      minmax: minMax,
    })();
  };

  const handleFocusHistogramInput = (serverLogMeta: GlobalFilterItem['serverLogMeta'], minMax: 'min' | 'max') => {
    clientLogProtobuf(ProductListPageLog.SearchTextFieldPriceRange.click, {
      containerDetail: DEFAULT_CONTAINER_DETAIL,
      logMeta: serverLogMeta,
      minmax: minMax,
    })();
  };

  const handleClickSubmit = (resultCount: number | undefined) => {
    clientLogProtobuf(ProductListPageLog.SearchButtonFilterListComplete.click, {
      containerDetail: DEFAULT_CONTAINER_DETAIL,
      logMeta: { ...searchCountData?.serverLogMeta, resultCount },
    })();
  };

  const handleImprNoResult = () => {
    clientLogProtobuf(ProductListPageLog.SearchLabelNoResult.impression, {
      containerDetail: DEFAULT_CONTAINER_DETAIL,
      logMeta: searchCountData?.serverLogMeta,
    })();
  };

  return (
    <GlobalFilterModal
      isOpen={!!open}
      description={globalFilterPriceRangeText}
      countData={searchCountData}
      closeModal={() => onCloseModal?.()}
      onImpressionFilter={handleImprFilter}
      onResetButtonClick={handleReset}
      onMoreButtonClick={handleMoreClick}
      onSubmitButtonClick={handleClickSubmit}
      onImpressionNoResult={handleImprNoResult}
      onClickHistogramHandle={handleClickHistogram}
      onFocusHistogramInput={handleFocusHistogramInput}
    />
  );
};
